export const M9_UNIT_1 = {
  id: "m9-u1-health-vocab",
  title: "Unit 1: Health Vocabulary",
  iconType: "medkit",
  grammarText: "Unit focus: Essential health vocabulary for international travel - symptoms, treatments, medical visits, and wellness. Natural integration of present perfect and modals.",
  
  sets: [

    // ═══════════════════════════════════════════════════════════════
    // SET 1: Airport Emergency - London Heathrow
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s1",
      title: "Set 1: Feeling Unwell at the Airport",
      context: `**Aeropuerto de Londres - Heathrow Terminal 5**

Acabas de aterrizar después de un vuelo de 12 horas. Te sientes fatal: tu garganta duele, tu cabeza late, y tienes escalofríos. Necesitas encontrar el medical center del aeropuerto.

**London Airport - Heathrow Terminal 5**

You've just landed after a 12-hour flight. You feel terrible: your throat hurts, your head is pounding, and you have chills. You need to find the airport medical center.`,
      
      questions: [
        {
          type: "match",
          q: "The nurse asks about your symptoms. Match the English term with what you're feeling:",
          pairs: [
            { left: "sore throat", right: "garganta que duele al tragar" },
            { left: "headache", right: "dolor de cabeza" },
            { left: "fever", right: "temperatura alta / calentura" },
            { left: "chills", right: "escalofríos / frío que te hace temblar" }
          ],
          a: "completed",
          tip: "'Sore' = painful. 'Fever' in US = 'high temperature' in UK!"
        },
        {
          type: "match",
          q: "Match more symptoms you might describe:",
          pairs: [
            { left: "runny nose", right: "nariz que gotea / moquea" },
            { left: "cough", right: "tos" },
            { left: "tired", right: "cansado / exhausto" },
            { left: "dizzy", right: "mareado" }
          ],
          a: "completed",
          successTip: "Americans say 'stuffy nose' (blocked), British say 'blocked nose'. Both right!"
        },
        {
          type: "mc",
          q: "Nurse: 'What brings you here today?'\nYou: 'I have a terrible ___ and my throat is really sore.'",
          options: ["headache", "backache", "stomachache"],
          a: "headache",
          tip: "What you feel in your head when it hurts"
        },
        {
          type: "input",
          q: "You tell the nurse: 'I think I have a ___. My temperature feels very high.'",
          a: "fever",
          tip: "High body temperature when sick"
        },
        {
          type: "order",
          q: "The nurse uses a thermometer and says:",
          words: ["You", "have", "a", "high", "temperature."],
          a: "You have a high temperature.",
          extra: ["fever", "cold", "the"],
          tip: "'Temperature' is more formal/medical than 'fever'"
        },
        {
          type: "mc",
          q: "The nurse asks: 'Are you ___ anywhere?'\nShe wants to know if you feel discomfort.",
          options: ["hurting", "painful", "sore"],
          a: "hurting",
          tip: "'Hurting' = feeling pain right now"
        },
        {
          type: "input",
          q: "You say: 'My nose won't stop running. I think I have a ___.'",
          a: "cold",
          tip: "Common illness with runny nose and sneezing"
        },
        {
          type: "order",
          q: "Describe your symptoms completely:",
          words: ["I", "have", "a", "sore", "throat", "and", "a", "fever."],
          a: "I have a sore throat and a fever.",
          extra: ["bad", "terrible"],
          tip: "Connect symptoms with 'and'"
        },
        {      
  type: "mc",
  q: "Nurse: 'Have you been feeling ___ for long?'",
  options: ["unwell", "badly", "good"],
  a: "unwell",
  tip: "After 'feeling' use adjective (unwell/sick/bad), not adverb (badly)"
        },
        {
          type: "input",
          q: "You explain: 'I've been feeling bad since yesterday. I think I caught the ___.'",
          a: "flu",
          tip: "Influenza = serious viral illness with fever",
          successTip: "'The flu' (US) vs 'flu' (UK). Americans always say 'THE flu'! Also: 'I caught a cold' but 'I have the flu'."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 2: Walk-in Clinic - Central London
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s2",
      title: "Set 2: At the Walk-in Clinic",
      context: `**Clínica de consulta inmediata - Centro de Londres**

El taxi te deja en una "walk-in clinic" (no necesitas cita). La recepcionista te pide que describas tus síntomas. El doctor está listo para verte.

**Walk-in Clinic - Central London**

The taxi drops you at a walk-in clinic (no appointment needed). The receptionist asks you to describe your symptoms. The doctor is ready to see you.`,
      
      questions: [
        {
          type: "match",
          q: "The receptionist gives you a form. Match medical terms:",
          pairs: [
            { left: "symptoms", right: "señales de que estás enfermo" },
            { left: "pain", right: "dolor / sufrimiento físico" },
            { left: "swollen", right: "hinchado / más grande de lo normal" },
            { left: "infection", right: "enfermedad causada por bacteria/virus" }
          ],
          a: "completed",
          tip: "'Symptom' = what you feel/see. 'Disease' = the actual illness"
        },
        {
          type: "match",
          q: "Match more medical vocabulary:",
          pairs: [
            { left: "appointment", right: "cita médica programada" },
            { left: "examination", right: "revisión médica / checkup" },
            { left: "treatment", right: "tratamiento / cura" },
            { left: "diagnosis", right: "identificación de la enfermedad" }
          ],
          a: "completed",
          successTip: "'Walk-in clinic' = no appointment needed. 'GP' (UK) = General Practitioner = family doctor!"
        },
        {
          type: "mc",
          q: "Doctor: 'Where does it ___?'\nHe's asking about the location of your discomfort.",
          options: ["hurt", "pain", "ache"],
          a: "hurt",
          tip: "'Hurt' as a verb = to cause pain"
        },
        {
          type: "input",
          q: "Doctor: 'When did your ___ start?'\nHe wants to know when you first felt sick.",
          a: "symptoms",
          tip: "Plural! Signs of illness"
        },
        {
          type: "order",
          q: "You explain the timeline:",
          words: ["I've", "been", "feeling", "sick", "for", "two", "days."],
          a: "I've been feeling sick for two days.",
          extra: ["since", "from"],
          tip: "Present perfect continuous for duration"
        },
        {
          type: "mc",
          q: "Doctor touches your neck: 'Your glands are ___.' (larger than normal)",
          options: ["swollen", "painful", "broken"],
          a: "swollen",
          tip: "Swollen = enlarged due to infection/injury"
        },
        {
          type: "input",
          q: "Doctor: 'Open wide, please.' He looks at your throat and says: 'You have an ___.'",
          a: "infection",
          tip: "Bacterial or viral illness"
        },
        {
          type: "order",
          q: "The doctor explains:",
          words: ["It", "looks", "like", "tonsillitis."],
          a: "It looks like tonsillitis.",
          extra: ["a", "the", "an"],
          tip: "Tonsillitis = throat infection"
        },
        {
          type: "mc",
          q: "Doctor: 'I'm going to prescribe some ___.'",
          options: ["medicine", "treatment", "appointment"],
          a: "medicine",
          alts: ["medication","pills","drugs","meds"],
          tip: "General term for medication"
        },
        {
          type: "input",
          q: "Receptionist: 'You don't need another ___. Just rest and take the medicine.'",
          a: "appointment",
          alts: ["visit","checkup","examination","consultation","consult"],
          tip: "No follow-up visit needed",
          successTip: "British say 'book an appointment'. Americans say 'make an appointment'. Both work everywhere!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 3: Pharmacy - Paris
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s3",
      title: "Set 3: Getting Medicine at the Pharmacy",
      context: `**Farmacia - Champs-Élysées, París**

Tienes la receta del doctor. Entras a una "pharmacie" con una cruz verde. El farmacéutico habla inglés y te explica cómo tomar la medicina.

**Pharmacy - Champs-Élysées, Paris**

You have the doctor's prescription. You enter a pharmacie with a green cross. The pharmacist speaks English and explains how to take the medicine.`,
      
      questions: [
        {
          type: "match",
          q: "The pharmacist shows you different medicines. Match the terms:",
          pairs: [
            { left: "pills / tablets", right: "pastillas / comprimidos" },
            { left: "prescription", right: "receta médica" },
            { left: "medicine / medication", right: "medicamento / medicina" },
            { left: "dose", right: "cantidad que debes tomar" }
          ],
          a: "completed",
          tip: "'Pills' (US) = 'tablets' (UK). Same thing!"
        },
        {
          type: "match",
          q: "Match more pharmacy vocabulary:",
          pairs: [
            { left: "painkillers", right: "analgésicos / para el dolor" },
            { left: "antibiotic", right: "antibiótico / mata bacterias" },
            { left: "syrup", right: "jarabe" },
            { left: "capsule", right: "cápsula" }
          ],
          a: "completed",
          successTip: "'Paracetamol' (UK/Europe) = 'Tylenol' (US brand) = 'acetaminophen' (US generic). Different names, same drug!"
        },
        {
          type: "mc",
          q: "Pharmacist: 'Take two ___ twice a day with water.'",
          options: ["pills", "medicines", "prescriptions"],
          a: "pills",
          tip: "Individual pieces of medication"
        },
        {
          type: "input",
          q: "Pharmacist: 'This is an ___. It will kill the bacteria in your throat.'",
          a: "antibiotic",
          tip: "Medication that fights bacterial infections"
        },
        {
          type: "order",
          q: "The pharmacist gives instructions:",
          words: ["Take", "them", "with", "food."],
          a: "Take them with food.",
          extra: ["after", "before", "without"],
          tip: "Important for stomach protection"
        },
        {
          type: "mc",
          q: "Pharmacist: 'Finish the whole ___, even if you feel better.' (treatment course)",
          options: ["prescription", "medicine", "appointment"],
          a: "prescription",
          tip: "Complete all the medicine prescribed"
        },
        {
          type: "input",
          q: "You ask: 'Can I take ___ for my headache too?' (pain medicine)",
          a: "painkillers",
          alts: ["antihistamines","analgesics","analgesic","aspirin","ibuprofen","acetaminophen","paracetamol"],
          tip: "Medicine to reduce pain"
        },
        {
          type: "order",
          q: "Pharmacist warns you:",
          words: ["Don't", "drink", "alcohol", "with", "these."],
          a: "Don't drink alcohol with these.",
          extra: ["any", "much"],
          tip: "Common medical advice with antibiotics"
        },
        {
          type: "mc",
          q: "You ask about side effects: 'Will these make me ___?' (sleepy)",
          options: ["drowsy", "sick", "better"],
          a: "drowsy",
          tip: "Drowsy = sleepy as a side effect"
        },
        {
          type: "input",
          q: "Pharmacist: 'Take one ___ every 6 hours.' (amount of medicine)",
          a: "dose",
          alts: ["dosage","pill","tablet","capsule"],
          tip: "The amount you take at one time",
          successTip: "'Twice daily' = '2x/day' on labels. 'TID' = 3 times daily. Medical shorthand is universal!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 4: Emergency Room - New York
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s4",
      title: "Set 4: Accident at the Hospital",
      context: `**Sala de emergencias - Manhattan, Nueva York**

Resbalaste en las escaleras del hotel. Tu muñeca está muy hinchada y no puedes moverla. El taxi te trae al ER (Emergency Room) del hospital más cercano.

**Emergency Room - Manhattan, New York**

You slipped on the hotel stairs. Your wrist is very swollen and you can't move it. The taxi brings you to the nearest hospital ER (Emergency Room).`,
      
      questions: [
        {
          type: "match",
          q: "The ER nurse assesses your injury. Match the medical terms:",
          pairs: [
            { left: "broken bone", right: "hueso roto / fractura" },
            { left: "sprain", right: "torcedura / ligamento estirado" },
            { left: "x-ray", right: "radiografía" },
            { left: "cast", right: "yeso / férula" }
          ],
          a: "completed",
          tip: "'Broken' = fractured. Same meaning!"
        },
        {
          type: "match",
          q: "Match more injury vocabulary:",
          pairs: [
            { left: "stitches", right: "puntos / suturas" },
            { left: "bandage", right: "venda" },
            { left: "wound", right: "herida" },
            { left: "bruise", right: "moretón / morado" }
          ],
          a: "completed",
          successTip: "'ER' (US) = 'A&E' (UK: Accident & Emergency). Same thing, different countries!"
        },
        {
          type: "mc",
          q: "Nurse: 'We need to take an ___ to see if it's broken.'",
          options: ["x-ray", "appointment", "operation"],
          a: "x-ray",
          tip: "Medical image showing bones"
        },
        {
          type: "input",
          q: "Doctor looks at the x-ray: 'Good news! It's just a ___. Not broken.'",
          a: "sprain",
          tip: "Injured ligament, not broken bone"
        },
        {
          type: "order",
          q: "The doctor explains your injury: 'Tu muñeca está torcida, no rota.'",
          words: ["Your", "wrist", "is", "sprained,", "not", "broken."],
          a: "Your wrist is sprained, not broken.",
          extra: ["badly", "very"],
          tip: "Sprain = ligament injury"
        },
        {
          type: "mc",
          q: "Nurse wraps your wrist: 'This ___ will support it while it heals.'",
          options: ["bandage", "medicine", "x-ray"],
          a: "bandage",
          tip: "Material wrapped around injuries"
        },
        {
          type: "input",
          q: "Doctor: 'Your wrist should ___ in about two weeks with rest.'",
          a: "heal",
          tip: "To recover / get better naturally"
        },
        {
          type: "order",
          q: "You ask about recovery time:",
          words: ["How", "long", "will", "it", "take", "to", "heal?"],
          a: "How long will it take to heal?",
          extra: ["much", "many"],
          tip: "Common question about recovery"
        },
        {
          type: "mc",
          q: "Earlier, you cut your hand badly. The doctor put in five ___ to close it.",
          options: ["stitches", "bandages", "x-rays"],
          a: "stitches",
          tip: "Medical thread to close wounds"
        },
        {
          type: "input",
          q: "Doctor: 'Take these ___ if the pain gets bad.' (pain medicine)",
          a: "painkillers",
          tip: "Medicine to reduce pain",
          successTip: "'Tylenol' (US) = 'paracetamol' (UK). 'Advil' (US) = 'ibuprofen' (everywhere). Know both names when traveling!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 5: Doctor's Checkup - Tokyo
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s5",
      title: "Set 5: Medical Examination",
      context: `**Clínica internacional - Shibuya, Tokio**

Tienes seguro de viaje y decides hacer un checkup completo. La clínica es moderna y todos los doctores hablan inglés. Es hora de tu examen médico.

**International Clinic - Shibuya, Tokyo**

You have travel insurance and decide to get a complete checkup. The clinic is modern and all doctors speak English. It's time for your medical examination.`,
      
      questions: [
        {
          type: "match",
          q: "The nurse prepares medical equipment. Match the tools:",
          pairs: [
            { left: "thermometer", right: "termómetro / mide temperatura" },
            { left: "blood test", right: "examen de sangre" },
            { left: "test results", right: "resultados de los exámenes" },
            { left: "checkup", right: "revisión médica general" }
          ],
          a: "completed",
          tip: "'Checkup' = routine medical examination"
        },
        {
          type: "match",
          q: "Match more examination vocabulary:",
          pairs: [
            { left: "blood pressure", right: "presión arterial" },
            { left: "heart rate", right: "ritmo cardíaco / pulso" },
            { left: "weight", right: "peso" },
            { left: "height", right: "altura / estatura" }
          ],
          a: "completed",
          successTip: "Japanese clinics use 'cm' and 'kg'. US uses 'feet/inches' and 'lbs'. Always clarify units!"
        },
        {
          type: "mc",
          q: "Nurse: 'I'll check your temperature with this ___.'",
          options: ["thermometer", "x-ray", "bandage"],
          a: "thermometer",
          tip: "Device to measure body temperature"
        },
        {
          type: "input",
          q: "Nurse: 'We need to take a ___ test to check your cholesterol levels.'",
          a: "blood",
          tip: "Medical examination of your blood"
        },
        {
          type: "order",
          q: "The doctor explains:",
          words: ["Your", "test", "results", "look", "good."],
          a: "Your test results look good.",
          extra: ["are", "very", "really"],
          tip: "Results = findings from tests"
        },
        {
          type: "mc",
          q: "Doctor checks your file: 'Have you had any ___ recently?' (past illnesses)",
          options: ["infections", "appointments", "treatments"],
          a: "infections",
          tip: "Past bacterial or viral illnesses"
        },
        {
          type: "input",
          q: "You mention: 'I had an ___ last month. I took antibiotics for it.'",
          a: "infection",
          tip: "Illness caused by bacteria/virus"
        },
        {
          type: "order",
          q: "Doctor asks about allergies:",
          words: ["Are", "you", "allergic", "to", "anything?"],
          a: "Are you allergic to anything?",
          extra: ["have", "do"],
          tip: "Important medical history question"
        },
        {
          type: "mc",
          q: "You explain: 'I have an ___ to penicillin.' (bad reaction)",
          options: ["allergy", "infection", "appointment"],
          a: "allergy",
          tip: "Bad physical reaction to substances"
        },
        {
          type: "input",
          q: "Doctor: 'Everything looks healthy. Your next ___ should be in one year.'",
          a: "checkup",
          tip: "Routine medical examination",
          successTip: "'Physical' (US) = 'medical' (UK) = checkup. Different words, same exam!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 6: Hotel Room Recovery - Sydney
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s6",
      title: "Set 6: Recovery and Wellness",
      context: `**Habitación de hotel - Sydney, Australia**

El doctor te dijo que descanses. Estás en tu hotel recuperándote. La recepción te envió té, agua, y comida saludable. Necesitas seguir las instrucciones del doctor.

**Hotel Room - Sydney, Australia**

The doctor told you to rest. You're in your hotel recovering. Reception sent up tea, water, and healthy food. You need to follow the doctor's instructions.`,
      
      questions: [
        {
          type: "match",
          q: "The doctor's note explains recovery steps. Match the advice:",
          pairs: [
            { left: "rest", right: "descansar / no hacer actividad" },
            { left: "recovery", right: "recuperación / proceso de mejora" },
            { left: "heal", right: "sanar / curarse" },
            { left: "feel better", right: "sentirse mejor" }
          ],
          a: "completed",
          tip: "'Rest' = no activity. 'Recovery' = getting healthy again"
        },
        {
          type: "match",
          q: "Match wellness vocabulary:",
          pairs: [
            { left: "drink water", right: "beber agua / hidratarse" },
            { left: "lie down", right: "acostarse / estar horizontal" },
            { left: "take it easy", right: "tomarlo con calma" },
            { left: "get well", right: "mejorarse / recuperarse" }
          ],
          a: "completed",
          successTip: "'Get well soon!' = universal greeting card phrase. Say it to sick friends in English!"
        },
        {
          type: "mc",
          q: "Doctor's note: 'You need complete ___ for three days.'",
          options: ["rest", "medicine", "exercise"],
          a: "rest",
          tip: "Time to recover without activity"
        },
        {
          type: "input",
          q: "The note says: 'Drink at least 2 liters of ___ daily.'",
          a: "water",
          tip: "Essential for recovery and hydration"
        },
        {
          type: "order",
          q: "Doctor's advice:",
          words: ["Stay", "in", "bed", "and", "rest."],
          a: "Stay in bed and rest.",
          extra: ["lie", "sleep"],
          tip: "Common recovery instruction"
        },
        {
          type: "mc",
          q: "After a few days, you're making a good ___. (getting better)",
          options: ["recovery", "rest", "appointment"],
          a: "recovery",
          tip: "Process of returning to health"
        },
        {
          type: "input",
          q: "Your wound is starting to ___. (get better naturally)",
          a: "heal",
          tip: "Natural process of body repair"
        },
        {
          type: "order",
          q: "You text your friend:",
          words: ["I'm", "feeling", "much", "better", "now."],
          a: "I'm feeling much better now.",
          extra: ["more", "very", "so"],
          tip: "'Much better' = significant improvement"
        },
        {
          type: "mc",
          q: "Tomorrow you can finally leave the hotel. You're almost ___!",
          options: ["recovered", "rested", "healing"],
          a: "recovered",
          tip: "Back to full health"
        },
        {
          type: "input",
          q: "Friend texts: 'Get ___ soon!' (common phrase wishing recovery)",
          a: "well",
          tip: "Standard phrase for sick people",
          successTip: "Australians say 'Good on ya!' when you recover. Americans say 'Way to go!'. British might say 'Well done!' Different encouragement, same love!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 7: Gym and Wellness - Barcelona
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s7",
      title: "Set 7: Health and Fitness",
      context: `**Gimnasio - Barceloneta, Barcelona**

Ya te recuperaste completamente. Quieres ponerte en forma. Te inscribes en un gym cerca de la playa. El entrenador personal habla inglés y te da consejos de wellness.

**Gym - Barceloneta, Barcelona**

You've fully recovered. You want to get in shape. You join a gym near the beach. The personal trainer speaks English and gives you wellness advice.`,
      
      questions: [
        {
          type: "match",
          q: "The trainer explains fitness vocabulary:",
          pairs: [
            { left: "exercise", right: "hacer ejercicio / actividad física" },
            { left: "work out", right: "entrenar / hacer ejercicio" },
            { left: "lose weight", right: "bajar de peso / adelgazar" },
            { left: "gain weight", right: "subir de peso / engordar" }
          ],
          a: "completed",
          tip: "'Exercise' = general. 'Work out' = go to gym"
        },
        {
          type: "match",
          q: "Match more wellness vocabulary:",
          pairs: [
            { left: "get in shape", right: "ponerse en forma" },
            { left: "stay healthy", right: "mantenerse saludable" },
            { left: "build muscle", right: "desarrollar músculo" },
            { left: "burn calories", right: "quemar calorías" }
          ],
          a: "completed",
          successTip: "'Gym' everywhere. But 'fitness center' (US formal), 'leisure centre' (UK), 'sports club' (international). Know all terms!"
        },
        {
          type: "mc",
          q: "Trainer: 'If you want to ___ weight, you need to exercise regularly.'",
          options: ["lose", "gain", "make"],
          a: "lose",
          tip: "Reduce body weight"
        },
        {
          type: "input",
          q: "Trainer: 'You should ___ at least 3 times a week.' (do physical activity)",
          a: "exercise",
          tip: "General term for physical activity"
        },
        {
          type: "order",
          q: "Trainer's advice:",
          words: ["Drink", "water", "before,", "during,", "and", "after."],
          a: "Drink water before, during, and after.",
          extra: ["more", "lots", "much"],
          tip: "Hydration is essential for exercise"
        },
        {
          type: "mc",
          q: "You tell the trainer: 'I want to ___. I'm too skinny!' (increase weight)",
          options: ["gain weight", "lose weight", "exercise"],
          a: "gain weight",
          tip: "Increase body weight"
        },
        {
          type: "input",
          q: "Trainer: 'To build muscle, you need protein and ___.'",
          a: "exercise",
          tip: "Physical activity to develop muscles"
        },
        {
          type: "order",
          q: "Trainer warns:",
          words: ["Don't", "push", "yourself", "too", "hard."],
          a: "Don't push yourself too hard.",
          extra: ["very", "so"],
          tip: "Avoid injury by not overdoing it"
        },
        {
          type: "mc",
          q: "Trainer: 'Remember: staying ___ is a lifestyle, not a diet.'",
          options: ["healthy", "rested", "sick"],
          a: "healthy",
          tip: "In good physical condition"
        },
        {
          type: "input",
          q: "You say: 'I want to get in ___.' (become fit)",
          a: "shape",
          tip: "'Get in shape' = become physically fit",
          successTip: "'Gym rat' (US slang) = someone who goes to gym every day. 'Fitness fanatic' (UK) = same thing. Both funny but compliments!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 8: Food Poisoning - Bangkok
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s8",
      title: "Set 8: Stomach Problems",
      context: `**Hotel en Bangkok - 3:00 AM**

Cenaste street food delicioso anoche. Pero ahora... oh no. Tu estómago duele muchísimo. Corres al baño. Necesitas llamar a recepción para pedir ayuda médica urgente.

**Hotel in Bangkok - 3:00 AM**

You had delicious street food last night. But now... oh no. Your stomach hurts terribly. You run to the bathroom. You need to call reception for urgent medical help.`,
      
      questions: [
        {
          type: "match",
          q: "You describe your symptoms to the hotel doctor:",
          pairs: [
            { left: "stomach ache", right: "dolor de estómago" },
            { left: "vomit / throw up", right: "vomitar / devolver" },
            { left: "nausea", right: "náuseas / ganas de vomitar" },
            { left: "diarrhea", right: "diarrea" }
          ],
          a: "completed",
          tip: "'Vomit' = formal. 'Throw up' = informal. Both correct!"
        },
        {
          type: "match",
          q: "Match more stomach illness vocabulary:",
          pairs: [
            { left: "food poisoning", right: "intoxicación alimentaria" },
            { left: "upset stomach", right: "estómago revuelto" },
            { left: "cramps", right: "calambres / dolor intenso" },
            { left: "dehydrated", right: "deshidratado / sin agua" }
          ],
          a: "completed",
          successTip: "'Tummy ache' (British/kids) = 'stomach ache' (everyone). Adults say 'my stomach hurts' or 'upset stomach'!"
        },
        {
          type: "mc",
          q: "You tell reception: 'I have terrible ___. My stomach is killing me!'",
          options: ["stomach ache", "headache", "backache"],
          a: "stomach ache",
          tip: "Pain in your stomach/abdomen"
        },
        {
          type: "input",
          q: "Reception asks: 'Have you been ___?' (throwing up)",
          a: "vomiting",
          tip: "Present perfect continuous form"
        },
        {
          type: "order",
          q: "You explain:",
          words: ["I", "think", "I", "have", "food", "poisoning."],
          a: "I think I have food poisoning.",
          extra: ["bad", "terrible"],
          tip: "Illness from contaminated food"
        },
        {
          type: "mc",
          q: "Doctor arrives: 'You're ___. You need to drink lots of water.'",
          options: ["dehydrated", "infected", "allergic"],
          a: "dehydrated",
          tip: "Lost too much water from body"
        },
        {
          type: "input",
          q: "Doctor: 'This medicine will help with the ___.' (feeling of wanting to vomit)",
          a: "nausea",
          tip: "Feeling sick to your stomach"
        },
        {
          type: "order",
          q: "Doctor's advice:",
          words: ["Don't", "eat", "anything", "for", "12", "hours."],
          a: "Don't eat anything for 12 hours.",
          extra: ["solid", "much"],
          tip: "Let stomach rest and recover"
        },
        {
          type: "mc",
          q: "Doctor: 'Stick to bland food when you start eating. Nothing ___.'",
          options: ["spicy", "sweet", "healthy"],
          a: "spicy",
          tip: "Avoid irritating food"
        },
        {
          type: "input",
          q: "Two days later, your upset ___ is finally better.",
          a: "stomach",
          tip: "Stomach that was sick",
          successTip: "'Montezuma's revenge' (US/Mexico), 'Delhi belly' (India), 'Bali belly' (Indonesia) = traveler's diarrhea. Every country has slang for it!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 9: Allergic Reaction - Berlin
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s9",
      title: "Set 9: Allergies and Skin Problems",
      context: `**Parque en Berlín - Primavera**

Es un día hermoso en Tiergarten. Pero después de 30 minutos, tus ojos pican, estornudas constantemente, y te salen ronchas rojas. La farmacia está cerca.

**Park in Berlin - Spring**

It's a beautiful day in Tiergarten. But after 30 minutes, your eyes itch, you're sneezing constantly, and you get red welts. The pharmacy is nearby.`,
      
      questions: [
        {
          type: "match",
          q: "You describe your symptoms to the pharmacist:",
          pairs: [
            { left: "allergy", right: "alergia / reacción negativa" },
            { left: "hay fever", right: "alergia al polen / fiebre del heno" },
            { left: "rash", right: "sarpullido / manchas rojas" },
            { left: "itchy", right: "que pica / da comezón" }
          ],
          a: "completed",
          tip: "'Hay fever' = seasonal pollen allergy"
        },
        {
          type: "match",
          q: "Match more allergy vocabulary:",
          pairs: [
            { left: "swollen", right: "hinchado / inflamado" },
            { left: "sneeze", right: "estornudar" },
            { left: "watery eyes", right: "ojos llorosos" },
            { left: "antihistamine", right: "antihistamínico / medicina para alergias" }
          ],
          a: "completed",
          successTip: "'Allergic' takes 'to': 'I'm allergic TO pollen'. Never 'allergic OF' or 'allergic FOR'!"
        },
        {
          type: "mc",
          q: "Pharmacist: 'It sounds like you have ___.' (pollen allergy)",
          options: ["hay fever", "food poisoning", "the flu"],
          a: "hay fever",
          tip: "Seasonal allergy to pollen"
        },
        {
          type: "input",
          q: "You show your arm: 'Look at this ___. It's so itchy!' (red skin spots)",
          a: "rash",
          tip: "Skin irritation with red spots"
        },
        {
          type: "order",
          q: "You explain your symptoms:",
          words: ["My", "eyes", "are", "really", "itchy."],
          a: "My eyes are really itchy.",
          extra: ["very", "so"],
          tip: "Itchy = causes desire to scratch"
        },
        {
          type: "mc",
          q: "Pharmacist: 'Your face looks ___. That's a classic allergy symptom.'",
          options: ["swollen", "broken", "infected"],
          a: "swollen",
          tip: "Enlarged due to allergic reaction"
        },
        {
          type: "input",
          q: "Pharmacist: 'Take this ___. It will reduce the allergic reaction.'",
          a: "antihistamine",
          tip: "Medicine for allergies"
        },
        {
          type: "order",
          q: "Pharmacist's advice:",
          words: ["Stay", "indoors", "when", "pollen", "is", "high."],
          a: "Stay indoors when pollen is high.",
          extra: ["inside", "at", "home"],
          tip: "Avoid allergen exposure"
        },
        {
          type: "mc",
          q: "You ask: 'Am I ___ to something in the park?'",
          options: ["allergic", "infected", "sick"],
          a: "allergic",
          tip: "'Allergic to' = having an allergy to"
        },
        {
          type: "input",
          q: "Pharmacist: 'Yes, you're allergic to ___. It's common in spring.'",
          a: "pollen",
          tip: "Plant particles that cause hay fever",
          successTip: "'Bless you!' (US) or 'Gesundheit!' (German origin, used everywhere) when someone sneezes. Universal politeness!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // SET 10: Telemedicine - International
    // ═══════════════════════════════════════════════════════════════
    {
      id: "m9-u1-s10",
      title: "Set 10: Online Doctor Consultation",
      context: `**Video call desde tu apartamento**

Estás en un país remoto y te sientes mal. Tu seguro de viaje incluye telemedicina. Abres la app y te conectas por video con un doctor que habla inglés. Necesitas describir todo claramente.

**Video call from your apartment**

You're in a remote country and feeling unwell. Your travel insurance includes telemedicine. You open the app and video-connect with an English-speaking doctor. You need to describe everything clearly.`,
      
      questions: [
        {
          type: "match",
          q: "The doctor asks you to describe everything. Review key terms:",
          pairs: [
            { left: "How long have you been feeling unwell?", right: "Duration question" },
            { left: "What are your symptoms?", right: "Describing what's wrong" },
            { left: "Have you taken any medicine?", right: "Treatment history" },
            { left: "Do you have any allergies?", right: "Medical history" }
          ],
          a: "completed",
          tip: "These are the 4 most common doctor questions"
        },
        {
          type: "match",
          q: "Match the symptom groups:",
          pairs: [
            { left: "Respiratory", right: "cough, sore throat, runny nose" },
            { left: "Digestive", right: "stomach ache, vomiting, nausea" },
            { left: "Pain", right: "headache, backache, cramps" },
            { left: "Skin", right: "rash, itchy, swollen" }
          ],
          a: "completed",
          successTip: "Group symptoms when describing! 'I have respiratory symptoms: cough and sore throat.' Doctors love organized info!"
        },
        {
          type: "mc",
          q: "Doctor: '___have you been feeling sick?' (duration)",
          options: ["How long", "How much", "How many"],
          a: "How long",
          tip: "Question about time duration"
        },
        {
          type: "input",
          q: "You answer: 'I've been feeling unwell for about ___ days.' (number)",
          a: "three",
          tip: "Any reasonable number works (accept: three/3/two/2/four/4)"
        },
        {
          type: "order",
          q: "Doctor asks about current symptoms:",
          words: ["What", "symptoms", "are", "you", "having", "right", "now?"],
          a: "What symptoms are you having right now?",
          extra: ["do", "have"],
          tip: "Present continuous for current situation"
        },
        {
          type: "mc",
          q: "You explain: 'I have a ___, a sore throat, and I feel very tired.'",
          options: ["fever", "bruise", "sprain"],
          a: "fever",
          tip: "High temperature symptom"
        },
        {
          type: "input",
          q: "Doctor: 'Have you ___ any medicine yet?' (past action)",
          a: "taken",
          tip: "Present perfect: Have you taken...?"
        },
        {
          type: "order",
          q: "You respond:",
          words: ["Yes,", "I've", "been", "taking", "painkillers."],
          a: "Yes, I've been taking painkillers.",
          extra: ["took", "have"],
          tip: "Present perfect continuous for ongoing action"
        },
        {
          type: "mc",
          q: "Doctor: 'Based on your symptoms, you probably have ___.'",
          options: ["the flu", "hay fever", "food poisoning"],
          a: "the flu",
          tip: "Fever + sore throat + tiredness = flu"
        },
        {
          type: "input",
          q: "Doctor prescribes: 'Get plenty of rest, drink water, and let your body ___.'",
          a: "heal",
          tip: "Recover naturally",
          successTip: "Telemedicine = 'telehealth' (US) = 'virtual doctor' (everywhere). Future of healthcare! Available worldwide now! 🌍💻"
        }
      ]
    }
  ],

  // ═══════════════════════════════════════════════════════════════
  // READING SET (Outside array) - Divided into passages
  // ═══════════════════════════════════════════════════════════════
  readingSet: {
    id: "m9-u1-reading",
    title: "Set 11: Reading - Healthcare Around the World",
    category: "reading",
    questions: [
      {
        type: "passage",
        passage: `Healthcare varies dramatically around the world. In countries like the UK and Canada, citizens receive free healthcare through public systems. People make appointments with their doctors and don't pay for most treatments. If someone has a fever or needs medicine, they visit their GP (General Practitioner) at no cost.`
      },
      {
        type: "mc",
        q: "In the UK and Canada, most healthcare is:",
        options: ["Free through public systems", "Paid through private insurance", "Very expensive"],
        a: "Free through public systems",
        tip: "Public healthcare = government-funded"
      },
      {
        type: "input",
        q: "In the UK, what does 'GP' stand for? General ___",
        a: "Practitioner",
        tip: "GP = family doctor in UK system"
      },
      {
        type: "mc",
        q: "According to the text, in these countries you don't pay for:",
        options: ["Most treatments", "All treatments", "No treatments"],
        a: "Most treatments",
        tip: "Text says 'most treatments', not 'all'"
      },
      {
        type: "passage",
        passage: `In contrast, the United States has a primarily private healthcare system. Americans usually have health insurance through their employers, but many people still pay significant amounts for appointments, medicine, and operations. An x-ray or blood test can cost hundreds of dollars without insurance.`
      },
      {
        type: "mc",
        q: "In the US, most people get health insurance through:",
        options: ["Their employers", "The government", "Private purchase"],
        a: "Their employers",
        tip: "Text states 'through their employers'"
      },
      {
        type: "input",
        q: "Without insurance in the US, medical tests can cost ___ of dollars.",
        a: "hundreds",
        tip: "Text mentions 'hundreds of dollars'"
      },
      {
        type: "passage",
        passage: `In some developing countries, access to healthcare is limited. People might travel hours to reach the nearest clinic. Common symptoms like headaches or stomachaches often go untreated because medicine is too expensive. When someone needs stitches or has a broken bone, they might not receive proper treatment.`
      },
      {
        type: "mc",
        q: "In developing countries, healthcare is often:",
        options: ["Limited and expensive", "Free and accessible", "Modern and quick"],
        a: "Limited and expensive",
        tip: "Text describes challenges"
      },
      {
        type: "input",
        q: "People in these countries might travel ___ to reach a clinic.",
        a: "hours",
        tip: "Text mentions 'travel hours'"
      },
      {
        type: "passage",
        passage: `However, technology is changing healthcare globally. Telemedicine allows doctors to diagnose patients remotely. Someone with a sore throat can send photos to a doctor and receive a prescription without leaving home. This is especially helpful in rural areas where appointments are difficult to arrange.`
      },
      {
        type: "mc",
        q: "Telemedicine allows doctors to:",
        options: ["Diagnose patients remotely", "Meet patients in person", "Only work in hospitals"],
        a: "Diagnose patients remotely",
        tip: "'Remotely' = from a distance"
      },
      {
        type: "input",
        q: "With telemedicine, you can get a ___ without leaving home.",
        a: "prescription",
        tip: "Doctor's written permission for medicine"
      },
      {
        type: "mc",
        q: "Telemedicine is especially useful in:",
        options: ["Rural areas", "Big cities", "Hospitals only"],
        a: "Rural areas",
        tip: "Text mentions 'especially helpful in rural areas'"
      },
      {
        type: "passage",
        passage: `Preventive care is becoming more important worldwide. Doctors now emphasize exercise, healthy eating, and drinking enough water to avoid illness. Many countries offer free vaccines to prevent infections. This focus on prevention helps people stay healthy and reduces the need for expensive treatments and operations later.`
      },
      {
        type: "mc",
        q: "Preventive care focuses on:",
        options: ["Avoiding illness before it happens", "Treating existing illnesses", "Emergency operations"],
        a: "Avoiding illness before it happens",
        tip: "'Preventive' = before getting sick"
      },
      {
        type: "input",
        q: "Free vaccines help prevent ___.",
        a: "infections",
        tip: "Vaccines target infections"
      },
      {
        type: "mc",
        q: "According to the text, preventive care helps:",
        options: ["Reduce need for expensive treatments", "Make treatments more expensive", "Replace all treatments"],
        a: "Reduce need for expensive treatments",
        tip: "Prevention saves money later"
      }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // REVIEW SET (Outside array) - 10 autocontenible questions
  // ═══════════════════════════════════════════════════════════════
  reviewSet: {
    id: "m9-u1-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
       
  type: "mc",
  q: "At Tokyo airport, the nurse checks your temperature with a thermometer: '39°C! You have a high ___ and sore throat.'",
  options: ["fever", "cold", "infection"],
  a: "fever",
  tip: "Fever = symptom of high temperature"
      },
      {
        type: "input",
        q: "At a London clinic, the doctor looks at your x-ray and says: 'It's just a ___, not broken.'",
        a: "sprain",
        tip: "Injured ligament, not fractured bone"
      },
      {
        type: "order",
        q: "The Paris pharmacist explains: 'Toma dos pastillas, dos veces al día con agua.'",
        words: ["take", "pills", "two", "twice","water","daily", "with"],
        a: "Take two pills twice daily with water.",
        extra: ["three", "once"],
      },
      {
        type: "mc",
        q: "In Bangkok, your stomach hurts badly after street food. You think you have:",
        options: ["food poisoning", "hay fever", "a sprain"],
        a: "food poisoning",
        tip: "Stomach problems after eating"
      },
      {
        type: "input",
        q: "The NYC hospital takes an ___ to check if your wrist is broken.",
        a: "x-ray",
        tip: "Image showing bones"
      },
      {
        type: "mc",
        q: "In Berlin park during spring, your eyes itch and you're sneezing constantly. You probably have:",
        options: ["hay fever", "headache", "food poisoning"],
        a: "hay fever",
        tip: "Spring allergy symptoms"
      },
      {
        type: "input",
        q: "Your Sydney doctor says: 'Your wound should ___ in two weeks.' (get better naturally)",
        a: "heal",
        tip: "Natural recovery process"
      },
      {
        type: "order",
        q: "Barcelona trainer's wellness advice:",
        words: ["Exercise", "regularly", "and", "drink", "lots", "of", "water."],
        a: "Exercise regularly and drink lots of water.",
        extra: ["always", "much"],
        tip: "Basic health recommendations"
      },
      {
        type: "mc",
        q: "You're doing a telemedicine call. The doctor asks: '___ have you been feeling unwell?'",
        options: ["How long", "How much", "How many"],
        a: "How long",
        tip: "Duration question"
      },
      {
        type: "input",
        q: "You've recovered! Your friend texts: 'Get ___ soon!'",
        a: "well",
         alts: ["better"],
        tip: "Standard phrase wishing recovery",
        successTip: "🏥🌍✨ UNIT 1 MASTERED! You can now describe symptoms (fever, sore throat, sprain), understand treatments (medicine, x-ray, stitches), navigate medical visits (appointments, checkups, prescriptions), and talk about wellness (exercise, rest, recovery) anywhere in the world! From London clinics to Bangkok hospitals to telemedicine calls - you're ready! Next up: Unit 2 (Must/Have to) will teach you about medical obligations and rules! 💊"
      }
    ]
  }
};
